<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type { NativeUpdater } from '../model.js';
import { useNativeUpdates } from '../lib/native-updates.js';
import AppIcon from './AppIcon.vue';
const props = defineProps<{ updater: NativeUpdater | undefined }>();
const { state, busy, check, install } = useNativeUpdates(props.updater);
const result = computed(() => state.result);
const percentage = computed(() =>
  state.total ? Math.min(100, Math.floor((state.downloaded / state.total) * 100)) : undefined,
);
const status = computed(() => {
  switch (state.phase) {
    case 'checking':
      return 'Checking for updates…';
    case 'available':
      return `Version ${state.result?.update?.version} is available.`;
    case 'current':
      return 'You’re up to date.';
    case 'unavailable':
      return state.result?.unavailableReason === 'managed_app_required'
        ? 'Open ATUM Remote from its tray or menu bar to update the installed app.'
        : state.result?.unavailableReason === 'signing_not_configured'
          ? 'This build is not configured for signed updates yet.'
          : 'In-app updates are not available for this platform.';
    case 'downloading':
      return percentage.value === undefined
        ? 'Downloading the signed update…'
        : `Downloading update… ${percentage.value}%`;
    case 'installing':
      return 'Installing the update…';
    case 'restarting':
      return 'Restarting ATUM Remote…';
    case 'error':
      return state.error;
    default:
      return props.updater
        ? 'Check for a new version of ATUM Remote.'
        : 'In-app updates are not available yet.';
  }
});
onMounted(() => {
  if (state.phase === 'idle') void check();
});
</script>

<template>
  <section class="panel" aria-labelledby="native-updates-title">
    <div class="section-head">
      <span class="section-icon"><AppIcon name="download" /></span>
      <div>
        <h2 id="native-updates-title">App updates</h2>
        <p v-if="result">Installed version {{ result.currentVersion }}</p>
        <p v-else>Keep this installation of ATUM Remote up to date.</p>
      </div>
    </div>
    <p :role="state.phase === 'error' ? 'alert' : 'status'">{{ status }}</p>
    <template v-if="state.phase === 'available' && result?.update">
      <p class="download-notes">
        {{ result.update.notes || 'Improvements and fixes for ATUM Remote.' }}
      </p>
      <p class="field-help">
        Installation will close connections and briefly stop hosting. ATUM Remote will restart with
        your saved settings and roles.
      </p>
      <button type="button" class="primary" @click="install">
        <AppIcon name="download" />Install and restart
      </button>
    </template>
    <progress
      v-if="state.phase === 'downloading'"
      :value="percentage"
      max="100"
      aria-label="Update download progress"
    />
    <button
      v-if="state.phase !== 'available'"
      type="button"
      :disabled="!props.updater || busy"
      @click="check"
    >
      {{ state.phase === 'error' ? 'Try again' : 'Check for updates' }}
    </button>
  </section>
</template>
